import { useState, useEffect, useCallback, useRef } from 'react';
import { X, MessageCircle, Bell } from 'lucide-react';
import { oznacitPrectenou, vzhledModulu } from '../lib/notifikace';
import NotifikaceKarta from './notifikace/Karta';
import useToastSwipe from '../hooks/useToastSwipe';

const MAX_TOASTS = 3;
const DURATION = 5000;
const SEEN_KEY = 'fofrlama_seen_toast_ids';
const SEEN_CAP = 500;

// Persistentní set ID-ček toastů, které už uživatel viděl. Toasty se stabilním
// ID (notif-{id}, chat-{zpravaId}) se zobrazí jen jednou — i napříč reloady,
// dokud uživatel localStorage nevyčistí. Action toasty s UUID/timestamp ID
// jsou pokaždé jiné, takže touto logikou neprojdou.
function loadSeen() {
    try {
        const raw = localStorage.getItem(SEEN_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
        return [];
    }
}
function saveSeen(list) {
    try {
        localStorage.setItem(SEEN_KEY, JSON.stringify(list.slice(-SEEN_CAP)));
    } catch (_) {}
}

function playSound(type) {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const now = ctx.currentTime;

        if (type === 'bell') {
            // Notifikace — dva tóny (ding-dong)
            [660, 880].forEach((freq, i) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.frequency.value = freq;
                osc.type = 'sine';
                const start = now + i * 0.15;
                gain.gain.setValueAtTime(0, start);
                gain.gain.linearRampToValueAtTime(0.07, start + 0.02);
                gain.gain.exponentialRampToValueAtTime(0.001, start + 0.3);
                osc.start(start);
                osc.stop(start + 0.3);
            });
        } else {
            // Chat — krátký pop
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.frequency.value = 800;
            osc.type = 'sine';
            gain.gain.setValueAtTime(0.06, now);
            gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
            osc.start(now);
            osc.stop(now + 0.2);
        }
    } catch (_) {}
}

export default function InAppToast({ onNavigate, isDark, isMobile }) {
    const [toasts, setToasts] = useState([]);
    const seenRef = useRef(new Set(loadSeen()));

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    // Poslouchat custom event
    useEffect(() => {
        const handler = (e) => {
            const { id, title, body, avatar, icon, modul, onClick } = e.detail || {};
            if (!id || !title) return;

            // Persistentní dedup — id už dříve zobrazené (i v jiné session) skipni
            if (seenRef.current.has(id)) return;
            seenRef.current.add(id);
            saveSeen([...seenRef.current]);

            setToasts(prev => {
                // In-memory dedup proti aktuálně viditelným toastům (paranoia, race během 1 tiku)
                if (prev.some(t => t.id === id)) return prev;
                playSound(icon === 'bell' ? 'bell' : 'chat');
                const next = [{ id, title, body, avatar, icon, modul, onClick, createdAt: Date.now() }, ...prev];
                return next.slice(0, MAX_TOASTS);
            });
        };
        window.addEventListener('in-app-toast', handler);
        return () => window.removeEventListener('in-app-toast', handler);
    }, []);

    // Auto-dismiss
    useEffect(() => {
        if (toasts.length === 0) return;
        const timers = toasts.map(t => {
            const elapsed = Date.now() - t.createdAt;
            const remaining = Math.max(DURATION - elapsed, 0);
            return setTimeout(() => removeToast(t.id), remaining);
        });
        return () => timers.forEach(clearTimeout);
    }, [toasts, removeToast]);

    if (toasts.length === 0) return null;

    // Na telefonu jsou tři toasty přes sebe čtvrtina obrazovky a překryjí to,
    // kvůli čemu je člověk v aplikaci. Dva stačí — starší stejně čekají
    // v Notifikacích.
    const videt = isMobile ? toasts.slice(0, 2) : toasts;

    const containerStyle = isMobile
        ? {
            position: 'fixed',
            // Pod hlavičkou aplikace (--hlavicka-mobil) s mezerou 8 px.
            top: 'calc(var(--hlavicka-mobil) + 8px)',
            left: 12, right: 12, zIndex: 9999,
        }
        : { position: 'fixed', bottom: 80, right: 24, zIndex: 9999, width: 360 };

    return (
        <div style={containerStyle} className="flex flex-col gap-2">
            {videt.map((t) => (
                <ToastPolozka key={t.id} t={t} isDark={isDark} isMobile={isMobile}
                    onZavrit={() => removeToast(t.id)}
                    onOtevrit={() => {
                        removeToast(t.id);
                        if (t.onClick?.notifikaceId) oznacitPrectenou(t.onClick.notifikaceId);
                        if (t.onClick?.page) onNavigate(t.onClick.page, t.onClick.params);
                        // Akce mimo navigaci (mobilní aplikace: stažení nového APK)
                        else if (typeof t.onClick === 'function') t.onClick();
                    }} />
            ))}

            {/* CSS animations */}
            <style>{`
                @keyframes toast-prichod {
                    from { opacity: 0; transform: ${isMobile ? 'translateY(-12px) scale(0.98)' : 'translateX(24px)'}; }
                    to { opacity: 1; transform: none; }
                }
                @keyframes toast-odpocet {
                    from { width: 100%; }
                    to { width: 0%; }
                }
            `}</style>
        </div>
    );
}

/**
 * Jeden toast — karta notifikace na skle, s odsunutím přejetím nahoru.
 *
 * Vlastní komponenta kvůli gestu: hook drží stav tahu a ten musí být per toast,
 * ne per seznam.
 */
function ToastPolozka({ t, isDark, isMobile, onZavrit, onOtevrit }) {
    const { props: gesto, style: gestoStyle } = useToastSwipe(onZavrit, { enabled: isMobile });

    // Barvu a ikonu bere toast z modulu notifikace — stejně jako karta
    // v Notifikacích. Zpráva od kolegy zůstává u avataru a brandové oranžové.
    const mod = t.modul ? vzhledModulu(t.modul) : null;
    const barva = mod?.color || '#FF8A00';
    const Ikona = mod?.Icon || (t.icon === 'bell' ? Bell : MessageCircle);

    return (
        <div style={{ animation: 'toast-prichod 0.28s cubic-bezier(0.22, 1, 0.36, 1)' }}>
            <div {...gesto} style={gestoStyle}>
                <NotifikaceKarta
                    varianta="sklo"
                    isDark={isDark}
                    Ikona={Ikona}
                    barva={barva}
                    avatar={t.avatar}
                    stitek={mod?.label || (t.icon === 'bell' ? null : 'Zpráva')}
                    titulek={t.title}
                    zprava={t.body}
                    naKliknuti={onOtevrit}
                    zavrit={onZavrit}
                    /* Odpočet do zmizení. Dřív to byl pruh přes celou spodní
                       hranu v plné brandové oranžové — u tří toastů nad sebou
                       tři blikající pruhy. Teď je to vlásek v barvě modulu
                       pod textem. */
                    pod={(
                        <div style={{ marginTop: 8, height: 2, borderRadius: 1, background: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(20,20,40,0.08)', overflow: 'hidden' }}>
                            <div style={{
                                height: '100%', borderRadius: 1,
                                background: barva, opacity: 0.5,
                                animation: `toast-odpocet ${DURATION}ms linear forwards`,
                            }} />
                        </div>
                    )}
                />
            </div>
        </div>
    );
}
